// TEAM AGGREGATION (docs/TEAM-REPORT-SPEC.md, deterministic layer) — the
// pure roll-up engine behind the owner's team report. Takes the round's
// member submissions (already shaped by the caller; NO prisma/app imports
// here) and computes per-member metrics + team roll-ups: overload,
// underutilization, task-overlap candidates, crack clusters, misalignment,
// delegate/drop/automate candidates, and the hire signal that feeds the
// HTVA CTA (decision #1).
//
// LOCKED DECISION #5: hours are the currency. No salary, no rates, no
// dollar math anywhere in this module — the founder product's cost
// sections do NOT carry over.
//
// Participation is a first-class output: when completion is under 70%,
// the report LEADS with "low participation is itself a finding" (plan §10
// honest-cons — degrade gracefully, never fake confidence). This module
// just computes the flag; the report layer enforces the ordering.

import { computeEnergyScoreBefore } from "../engine/energy";
import { getPillarLabel } from "../engine/pillars";
import { getFinalResolution } from "../engine/resolution";

// ── Input shapes ─────────────────────────────────────────────────────────
// One activity as the member submitted it. The resolution-relevant force_*
// fields ride along so getFinalResolution (the canonical resolver) decides
// delegate/drop/automate exactly the way the founder product does. Member
// audits don't capture revenue_impact/complexity, so the algorithmic base
// resolution usually abstains and the member's own "someone else should be
// doing this" flag (force_delegation) is the primary router — by design.

export type TeamActivityInput = {
  activity_name: string;
  hours_per_week: number | null;
  energy_impact: number | null; // -2..2
  skill: string | null; // Basic | Intermediate | Advanced
  enjoyment_rating: string | null; // canonical 4-string
  strategic_pillar: string | null;
  force_delegation: boolean;
  // GOAL-ALIGNMENT SIGNAL (#8 / R1): the member's own reason this task
  // matters to the company goal. Empty/one-word answers are LOW signal —
  // the goal-alignment lens buckets on why-strength (the AI does the
  // nuanced "does this actually drive the goal" judgment). Absent on
  // legacy rows.
  task_why?: string | null;
  // optional overrides / base-resolution signals (absent on member rows)
  ill_do_it?: boolean;
  force_plan?: boolean;
  force_drop?: boolean;
  force_must_delegate?: boolean;
  currently_doing?: boolean;
  revenue_impact?: string | null;
  complexity?: string | null;
  is_urgent?: boolean;
  is_important?: boolean;
  is_mandatory?: boolean;
};

// The manager-only "team & management" answers (memberAudit.RoleContext,
// mirrored here so the engine stays app-import-free). Present only for
// members who submitted the management section; null for ICs. team_load_read
// reuses the same LOAD_BANDS scale as members' own self-reports, which is
// what makes the manager↔report triangulation (#7) a like-for-like compare.
export type TeamRoleContext = {
  mgmt_share?: number | null;
  team_load_read?: string | null; // a LOAD_BAND — the manager's read of the team's load
  team_clarity_read?: number | null; // 1..5 — manager's read of the team's role clarity
  meeting_overhead_hours?: number | null;
  bottleneck?: string | null;
};

export type TeamMemberInput = {
  member_id: string;
  name: string;
  role_title: string | null;
  is_owner: boolean;
  // true only for a COMPLETED submission; non-participants carry no data
  participated: boolean;
  activities: TeamActivityInput[];
  capacity_hours: number | null;
  load_band: string | null; // underloaded|comfortable|full|overloaded|drowning
  role_clarity: { items?: Partial<Record<string, number>> } | null;
  friction: { cracks?: string; invisible?: string; drop?: string } | null;
  // ORG STRUCTURE (#7): the member this person reports to (Member.reports_to_id).
  // Null for top-of-tree / owner. Drives the manager↔report triangulation.
  reports_to_id?: string | null;
  // Manager-only management read (see TeamRoleContext); null for ICs.
  role_context?: TeamRoleContext | null;
};

// The company's stated goal — the yardstick the goal-alignment lens measures
// work against. Null/absent when the owner hasn't set one; the lens then
// degrades gracefully (skips, notes it) rather than judging work in a vacuum.
export type TeamGoalInput = {
  goal: string | null;
  vision?: string | null;
  timeframe?: string | null;
} | null;

export type TeamAggregationInput = {
  members: TeamMemberInput[];
  goal?: TeamGoalInput;
};

// ── Output shapes ────────────────────────────────────────────────────────

export type LoadFlag = "overloaded" | "underutilized" | "balanced" | "unknown";
export type MisalignmentRisk = "high" | "moderate" | "low" | "none";

export type ResolutionCandidate = {
  member_id: string;
  member_name: string;
  activity_name: string;
  hours_per_week: number;
};

export type MemberMetrics = {
  member_id: string;
  name: string;
  role_title: string | null;
  is_owner: boolean;
  participated: boolean;
  claimed_hours_per_week: number;
  capacity_hours: number | null;
  load_band: string | null;
  // claimed / stated capacity; null when capacity unknown
  load_index: number | null;
  load_flag: LoadFlag;
  energy: {
    energizing_hours: number;
    neutral_hours: number;
    draining_hours: number;
    draining_share: number; // 0..1 of claimed hours
    energy_score: number; // -100..100, engine normalization
  };
  misalignment: {
    misaligned_hours: number; // hours in low-skill and/or low-enjoyment tasks
    low_skill_hours: number;
    low_enjoyment_hours: number;
    share: number; // 0..1 of claimed hours
    risk: MisalignmentRisk;
    tasks: string[]; // misaligned task names, hours-desc
  };
  clarity_score: number | null; // 0..100
  resolutions: {
    delegate: { activity_name: string; hours_per_week: number }[];
    drop: { activity_name: string; hours_per_week: number }[];
    automate: { activity_name: string; hours_per_week: number }[];
  };
  top_tasks: { name: string; hours: number }[];
};

export type OverlapActivityRef = {
  member_id: string;
  member_name: string;
  activity_name: string;
  hours_per_week: number;
};

export type OverlapGroup = {
  label: string; // shortest activity name in the group
  activities: OverlapActivityRef[];
  member_count: number; // distinct members claiming this work
  total_hours_per_week: number;
};

export type CrackMention = {
  member_id: string;
  member_name: string;
  field: "cracks" | "invisible" | "drop";
  text: string;
};

export type CrackCluster = {
  label: string; // shortest mention text in the cluster
  mentions: CrackMention[];
  member_count: number;
  shared_by_multiple: boolean;
};

export type HireSignal = {
  total_hours_per_week: number;
  task_count: number;
  by_pillar: {
    pillar: string;
    hours_per_week: number;
    task_count: number;
    member_count: number;
    top_tasks: string[];
  }[];
  by_skill: { skill: string; hours_per_week: number }[];
  dominant_skill: string | null;
  suggested_role: string | null;
};

export type TeamParticipation = {
  invited: number;
  completed: number;
  rate: number; // 0..100
  low: boolean; // rate < 70 → the report must lead with this
  non_participants: { member_id: string; name: string; role_title: string | null }[];
};

// ── Goal-alignment lens (#8) ──────────────────────────────────────────────
// Deterministic PASS ONLY: buckets team hours by the STRENGTH of each task's
// "why" (missing / weak / strong), never by semantic alignment — judging
// whether a task actually drives the goal is the AI narrative's job. A task
// with a real, specific reason is a goal-aligned candidate; a task with a
// missing or throwaway why (especially a high-hours one) is a questionable
// candidate the owner should look at relative to the goal.

export type WhySignal = "strong" | "weak" | "missing";

export type GoalAlignmentFlag = {
  member_id: string;
  member_name: string;
  activity_name: string;
  hours_per_week: number;
  why: string | null;
  why_signal: WhySignal;
};

export type GoalAlignmentMemberRollup = {
  member_id: string;
  member_name: string;
  aligned_hours: number;
  questionable_hours: number;
};

export type TeamGoalAlignment = {
  enabled: boolean; // false when no company goal is set (lens skipped)
  goal: string | null;
  vision: string | null;
  timeframe: string | null;
  aligned_hours: number; // team hours on tasks with a strong why
  questionable_hours: number; // team hours on missing/weak-why tasks
  total_hours: number;
  aligned_share: number; // 0..1 of measured hours that are goal-aligned candidates
  by_member: GoalAlignmentMemberRollup[];
  flags: GoalAlignmentFlag[]; // every questionable task (missing/weak why), hours-desc
  drop_candidates: GoalAlignmentFlag[]; // top questionable-by-hours — "drop relative to the goal"
  note: string | null; // populated (with the reason) when the lens is disabled
};

// ── Manager↔report triangulation (#7) ─────────────────────────────────────
// Cross-checks each manager's stated read of their team's load/clarity
// against those reports' OWN self-reports. Surfaces the gap AND hardens
// against self-report fudging (cross-validation catches inflation in either
// direction). Degrades gracefully: managers with no reports present, or
// reports who didn't participate, are noted — never crashed on.

export type InconsistencyKind = "load" | "clarity";
export type InconsistencySeverity = "notable" | "stark";
export type InconsistencyDirection = "manager_under_reads" | "manager_over_reads";

export type LoadInconsistency = {
  kind: InconsistencyKind;
  manager_id: string;
  manager_name: string;
  report_id: string;
  report_name: string;
  manager_read: string; // e.g. "comfortable" or "4/5"
  report_self: string; // e.g. "drowning" or "2/5"
  gap: number; // step gap (load: 0..4) or point gap (clarity: 0..100)
  severity: InconsistencySeverity;
  direction: InconsistencyDirection;
  detail: string; // one plain sentence
};

export type ManagerTriangulation = {
  manager_id: string;
  manager_name: string;
  team_load_read: string | null;
  team_clarity_read: number | null;
  reports_present: number; // reports who participated + self-reported
  reports_missing: number; // reports who didn't participate (couldn't cross-check)
  missing_names: string[]; // names of reports we couldn't cross-check
  inconsistencies: LoadInconsistency[];
};

export type Inconsistencies = {
  enabled: boolean; // false when no manager submitted a management read
  managers: ManagerTriangulation[];
  items: LoadInconsistency[]; // flattened across managers, stark-first
  note: string | null; // populated (with the reason) when disabled
};

export type TeamAggregation = {
  participation: TeamParticipation;
  team: {
    total_claimed_hours_per_week: number;
    total_capacity_hours_per_week: number | null; // null when no participant stated capacity
    overloaded_count: number;
    underutilized_count: number;
    energy: {
      energizing_hours: number;
      neutral_hours: number;
      draining_hours: number;
      energy_score: number; // -100..100 across all participant hours
    };
  };
  members: MemberMetrics[];
  overloaded_members: MemberMetrics[];
  underutilized_members: MemberMetrics[];
  overlaps: OverlapGroup[];
  crack_clusters: CrackCluster[];
  misaligned_members: {
    member_id: string;
    name: string;
    misaligned_hours: number;
    share: number;
    risk: MisalignmentRisk;
    tasks: string[];
  }[];
  delegate_candidates: ResolutionCandidate[];
  drop_candidates: ResolutionCandidate[];
  automate_candidates: ResolutionCandidate[];
  hire_signal: HireSignal;
  goalAlignment: TeamGoalAlignment;
  inconsistencies: Inconsistencies;
};

// ── Tunable boundary constants (single place) ────────────────────────────

export const LOW_PARTICIPATION_THRESHOLD = 70; // percent — spec-pinned
const OVERLOAD_BANDS = ["overloaded", "drowning"];
const UNDERLOAD_BAND = "underloaded";
const LOAD_INDEX_OVERLOADED = 1.1; // claiming 110%+ of stated capacity
const LOAD_INDEX_UNDERUTILIZED = 0.7; // claiming under 70% of capacity
const MISALIGNMENT_HIGH = 0.4;
const MISALIGNMENT_MODERATE = 0.2;

// GOAL-ALIGNMENT (#8) — a "why" is low-signal when empty, too short, or a
// known throwaway. Mirrors src/lib/memberAudit.ts validateWhy (spec-pinned,
// re-implemented here because the engine stays app-import-free — same reason
// CLARITY_ITEMS is duplicated). Kept in sync with WHY_MIN_CHARS / WHY_JUNK.
const WHY_MIN_SIGNAL_CHARS = 12;
const WHY_JUNK_RE =
  /^(idk|dunno|n\/?a|none|nothing|my job|just my job|because|it'?s my job|task)\.?$/i;
const GOAL_DROP_CANDIDATE_LIMIT = 8;

// TRIANGULATION (#7) — LOAD_BANDS as an ordered severity scale (0 lightest,
// 4 heaviest) so a manager's read and a report's self-report compare on one
// axis. A gap of >= 2 steps is "notable", >= 3 is "stark". Clarity compares
// the manager's 1..5 read (mapped to 0..100) against the report's 0..100
// clarity score; a >= 40-point gap is notable, >= 60 is stark.
// Exported so the delta engine (teamDelta.ts) reads load shifts on the exact
// same ordered scale — a manager's read, a member's self-report, and a
// month-over-month load change all compare on one axis.
export const LOAD_SEVERITY_ORDER: Record<string, number> = {
  underloaded: 0,
  comfortable: 1,
  full: 2,
  overloaded: 3,
  drowning: 4,
};
const LOAD_GAP_NOTABLE = 2;
const LOAD_GAP_STARK = 3;
const CLARITY_GAP_NOTABLE = 40;
const CLARITY_GAP_STARK = 60;

const round1 = (n: number): number => Math.round(n * 10) / 10;
const round2 = (n: number): number => Math.round(n * 100) / 100;

// ── Role-clarity score ───────────────────────────────────────────────────
// Same formula as the member mini-report (src/lib/memberMiniReport.ts):
// mean of the four 5-point items mapped to 0..100, with pulled_into_others
// reverse-scored (5 = constantly pulled = LOW clarity). Re-implemented here
// because this package must stay free of app imports; the item list is
// spec-pinned (MEMBER-AUDIT-SPEC step 2) so drift risk is low.

const CLARITY_ITEMS = [
  "responsibilities",
  "success_definition",
  "pulled_into_others",
  "handoff_clarity",
] as const;

export function computeClarityScore(
  roleClarity: { items?: Partial<Record<string, number>> } | null | undefined,
): number | null {
  const items = roleClarity?.items;
  if (!items) return null;
  const vals: number[] = [];
  for (const key of CLARITY_ITEMS) {
    const raw = Number(items[key]);
    if (!Number.isInteger(raw) || raw < 1 || raw > 5) continue;
    const oriented = key === "pulled_into_others" ? 6 - raw : raw;
    vals.push(((oriented - 1) / 4) * 100);
  }
  if (vals.length !== CLARITY_ITEMS.length) return null;
  return Math.round(vals.reduce((s, v) => s + v, 0) / vals.length);
}

// ── Token normalization (overlap + crack matching) ───────────────────────
// Deliberately simple and inspectable: lowercase, strip punctuation, drop
// function words, singularize bare plurals. The AI pass canonicalizes
// labels later; this layer only flags CANDIDATES, so precision beats
// cleverness (no stemming beyond trailing-'s').

const STOPWORDS = new Set([
  "the",
  "a",
  "an",
  "and",
  "or",
  "of",
  "for",
  "to",
  "with",
  "on",
  "in",
  "at",
  "by",
  "from",
  "our",
  "my",
  "their",
  "his",
  "her",
  "its",
  "all",
  "any",
  "per",
  "up",
  "out",
  "new",
  "old",
  "weekly",
  "daily",
  "monthly",
]);

export function normalizeActivityTokens(name: string): string[] {
  const tokens = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .split(/\s+/)
    .filter((t) => t.length > 1 && !STOPWORDS.has(t))
    .map((t) => (t.length > 3 && t.endsWith("s") && !t.endsWith("ss") ? t.slice(0, -1) : t));
  return Array.from(new Set(tokens));
}

// Two token sets describe overlapping work when they share at least two
// meaningful tokens, or share one token that covers at least half of the
// smaller name ("Invoicing" vs "Invoicing clients"; "Chasing invoices" vs
// "Invoice chasing").
export function tokensOverlap(a: string[], b: string[]): boolean {
  if (a.length === 0 || b.length === 0) return false;
  const setB = new Set(b);
  const shared = a.filter((t) => setB.has(t)).length;
  if (shared >= 2) return true;
  return shared >= 1 && shared / Math.min(a.length, b.length) >= 0.5;
}

// ── Union-find (tiny, local) ─────────────────────────────────────────────

class UnionFind {
  private parent: number[];
  constructor(n: number) {
    this.parent = Array.from({ length: n }, (_, i) => i);
  }
  find(i: number): number {
    while (this.parent[i] !== i) {
      this.parent[i] = this.parent[this.parent[i] as number] as number;
      i = this.parent[i] as number;
    }
    return i;
  }
  union(a: number, b: number): void {
    const ra = this.find(a);
    const rb = this.find(b);
    if (ra !== rb) this.parent[rb] = ra;
  }
}

// ── Overlap detection ────────────────────────────────────────────────────

export function detectOverlaps(
  members: { member_id: string; name: string; activities: TeamActivityInput[] }[],
): OverlapGroup[] {
  type Node = OverlapActivityRef & { tokens: string[] };
  const nodes: Node[] = [];
  for (const m of members) {
    for (const a of m.activities) {
      const tokens = normalizeActivityTokens(a.activity_name);
      if (tokens.length === 0) continue;
      nodes.push({
        member_id: m.member_id,
        member_name: m.name,
        activity_name: a.activity_name,
        hours_per_week: round1(Number(a.hours_per_week) || 0),
        tokens,
      });
    }
  }

  const uf = new UnionFind(nodes.length);
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      // only cross-member matches create overlap edges — one member
      // listing near-duplicate tasks is their own bookkeeping issue
      const a = nodes[i] as Node;
      const b = nodes[j] as Node;
      if (a.member_id === b.member_id) continue;
      if (tokensOverlap(a.tokens, b.tokens)) uf.union(i, j);
    }
  }

  const groups = new Map<number, Node[]>();
  nodes.forEach((n, i) => {
    const root = uf.find(i);
    const list = groups.get(root) ?? [];
    list.push(n);
    groups.set(root, list);
  });

  const out: OverlapGroup[] = [];
  for (const list of groups.values()) {
    const distinctMembers = new Set(list.map((n) => n.member_id));
    if (distinctMembers.size < 2) continue;
    const label = list
      .map((n) => n.activity_name)
      .sort((a, b) => a.length - b.length || a.localeCompare(b))[0] as string;
    out.push({
      label,
      activities: list
        .map(({ tokens: _tokens, ...rest }) => rest)
        .sort(
          (a, b) =>
            b.hours_per_week - a.hours_per_week || a.member_name.localeCompare(b.member_name),
        ),
      member_count: distinctMembers.size,
      total_hours_per_week: round1(list.reduce((s, n) => s + n.hours_per_week, 0)),
    });
  }
  return out.sort(
    (a, b) => b.member_count - a.member_count || b.total_hours_per_week - a.total_hours_per_week,
  );
}

// ── Crack clustering (friction free-text) ────────────────────────────────
// Groups exact/near-duplicate mentions across members; the AI narrates.
// Near-duplicate = normalized-token Jaccard ≥ 0.5, or identical normalized
// strings. Singleton mentions still come back as clusters (member_count 1)
// so the narrative layer sees everything; shared_by_multiple marks the
// deterministically-confirmed team-level cracks.

export function clusterCracks(
  members: {
    member_id: string;
    name: string;
    friction: { cracks?: string; invisible?: string; drop?: string } | null;
  }[],
): CrackCluster[] {
  const FIELDS = ["cracks", "invisible", "drop"] as const;
  type Node = CrackMention & { tokens: string[] };
  const nodes: Node[] = [];
  for (const m of members) {
    if (!m.friction) continue;
    for (const field of FIELDS) {
      const text = (m.friction[field] ?? "").trim();
      if (!text) continue;
      nodes.push({
        member_id: m.member_id,
        member_name: m.name,
        field,
        text,
        tokens: normalizeActivityTokens(text),
      });
    }
  }

  const jaccard = (a: string[], b: string[]): number => {
    if (a.length === 0 || b.length === 0) return 0;
    const setB = new Set(b);
    const shared = a.filter((t) => setB.has(t)).length;
    return shared / (a.length + b.length - shared);
  };

  const uf = new UnionFind(nodes.length);
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      const same =
        (nodes[i] as Node).text.toLowerCase() === (nodes[j] as Node).text.toLowerCase() ||
        jaccard((nodes[i] as Node).tokens, (nodes[j] as Node).tokens) >= 0.5;
      if (same) uf.union(i, j);
    }
  }

  const groups = new Map<number, Node[]>();
  nodes.forEach((n, i) => {
    const root = uf.find(i);
    const list = groups.get(root) ?? [];
    list.push(n);
    groups.set(root, list);
  });

  const clusters: CrackCluster[] = [];
  for (const list of groups.values()) {
    const distinctMembers = new Set(list.map((n) => n.member_id));
    const label = list
      .map((n) => n.text)
      .sort((a, b) => a.length - b.length || a.localeCompare(b))[0] as string;
    clusters.push({
      label,
      mentions: list.map(({ tokens: _tokens, ...rest }) => rest),
      member_count: distinctMembers.size,
      shared_by_multiple: distinctMembers.size >= 2,
    });
  }
  return clusters.sort(
    (a, b) => b.member_count - a.member_count || b.mentions.length - a.mentions.length,
  );
}

// ── Per-member metrics ───────────────────────────────────────────────────

const LOW_ENJOYMENT_PREFIX = "don't like";

// Exported so the MEMBER's own report (src/lib/memberMiniReport.ts) is built
// from the exact same per-member computation the team roll-up uses — the
// member's energy score, clarity, misalignment, and delegate/drop resolutions
// can never drift from the numbers that feed the owner's team report.
export function computeMemberMetrics(m: TeamMemberInput): MemberMetrics {
  const acts = m.activities
    .map((a) => ({ ...a, hours: Number(a.hours_per_week) || 0 }))
    .filter((a) => a.activity_name);

  const claimed = acts.reduce((s, a) => s + a.hours, 0);
  const capacity = Number(m.capacity_hours) > 0 ? round1(Number(m.capacity_hours)) : null;
  const loadIndex = capacity ? round2(claimed / capacity) : null;

  // load flag — index OR self-reported band, whichever alarms first
  let loadFlag: LoadFlag = "unknown";
  const bandOver = OVERLOAD_BANDS.includes(m.load_band ?? "");
  const bandUnder = m.load_band === UNDERLOAD_BAND;
  if (bandOver || (loadIndex !== null && loadIndex >= LOAD_INDEX_OVERLOADED)) {
    loadFlag = "overloaded";
  } else if (bandUnder || (loadIndex !== null && loadIndex <= LOAD_INDEX_UNDERUTILIZED)) {
    loadFlag = "underutilized";
  } else if (loadIndex !== null || m.load_band) {
    loadFlag = "balanced";
  }

  // energy profile — engine normalization for the score (adapt hours into
  // the rova shape computeEnergyScoreBefore reads), simple hour splits
  // for the profile
  let energizing = 0;
  let neutral = 0;
  let draining = 0;
  for (const a of acts) {
    const e = a.energy_impact ?? 0;
    if (e > 0) energizing += a.hours;
    else if (e < 0) draining += a.hours;
    else neutral += a.hours;
  }
  const energyScore = computeEnergyScoreBefore(
    acts.map((a) => ({
      energy_impact: a.energy_impact ?? 0,
      delegation_plan: { rova_tracker: { diy_hours_monthly: a.hours } },
    })),
  );

  // misalignment — hours in low-skill and/or low-enjoyment tasks
  let lowSkill = 0;
  let lowEnjoy = 0;
  let misaligned = 0;
  const misalignedTasks: { name: string; hours: number }[] = [];
  for (const a of acts) {
    const isLowSkill = a.skill === "Basic";
    const isLowEnjoy = (a.enjoyment_rating ?? "").toLowerCase().startsWith(LOW_ENJOYMENT_PREFIX);
    if (isLowSkill) lowSkill += a.hours;
    if (isLowEnjoy) lowEnjoy += a.hours;
    if (isLowSkill || isLowEnjoy) {
      misaligned += a.hours;
      misalignedTasks.push({ name: a.activity_name, hours: a.hours });
    }
  }
  const misalignShare = claimed > 0 ? misaligned / claimed : 0;
  const risk: MisalignmentRisk =
    misaligned <= 0
      ? "none"
      : misalignShare >= MISALIGNMENT_HIGH
        ? "high"
        : misalignShare >= MISALIGNMENT_MODERATE
          ? "moderate"
          : "low";

  // delegate / drop / automate — canonical resolver, same as the founder
  // product. Member rows rarely satisfy the base algorithm's inputs, so
  // the member's own force flags do most of the routing (by design).
  const sessionCtx = { team_size_band: null, gross_revenue_band: null };
  const delegate: { activity_name: string; hours_per_week: number }[] = [];
  const drop: { activity_name: string; hours_per_week: number }[] = [];
  const automate: { activity_name: string; hours_per_week: number }[] = [];
  for (const a of acts) {
    const record = {
      ...a,
      currently_doing: a.currently_doing ?? true,
      energy_impact: a.energy_impact ?? 0,
      delegation_plan: { rova_tracker: { diy_hours_monthly: a.hours } },
    };
    const res = getFinalResolution(record, sessionCtx);
    const entry = { activity_name: a.activity_name, hours_per_week: round1(a.hours) };
    if (res === "Delegate" || res === "MustDelegate") delegate.push(entry);
    else if (res === "Drop") drop.push(entry);
    else if (res === "Plan") automate.push(entry);
  }
  const byHoursDesc = (a: { hours_per_week: number }, b: { hours_per_week: number }) =>
    b.hours_per_week - a.hours_per_week;
  delegate.sort(byHoursDesc);
  drop.sort(byHoursDesc);
  automate.sort(byHoursDesc);

  return {
    member_id: m.member_id,
    name: m.name,
    role_title: m.role_title,
    is_owner: m.is_owner,
    participated: m.participated,
    claimed_hours_per_week: round1(claimed),
    capacity_hours: capacity,
    load_band: m.load_band,
    load_index: loadIndex,
    load_flag: loadFlag,
    energy: {
      energizing_hours: round1(energizing),
      neutral_hours: round1(neutral),
      draining_hours: round1(draining),
      draining_share: claimed > 0 ? round2(draining / claimed) : 0,
      energy_score: energyScore,
    },
    misalignment: {
      misaligned_hours: round1(misaligned),
      low_skill_hours: round1(lowSkill),
      low_enjoyment_hours: round1(lowEnjoy),
      share: round2(misalignShare),
      risk,
      tasks: misalignedTasks.sort((a, b) => b.hours - a.hours).map((t) => t.name),
    },
    clarity_score: computeClarityScore(m.role_clarity),
    resolutions: { delegate, drop, automate },
    top_tasks: acts
      .filter((a) => a.hours > 0)
      .sort((a, b) => b.hours - a.hours)
      .slice(0, 5)
      .map((a) => ({ name: a.activity_name, hours: round1(a.hours) })),
  };
}

// Empty metrics for a non-participant — appears in the roster with the
// honest "didn't take part" shape rather than fake zero-scores.
function nonParticipantMetrics(m: TeamMemberInput): MemberMetrics {
  return {
    member_id: m.member_id,
    name: m.name,
    role_title: m.role_title,
    is_owner: m.is_owner,
    participated: false,
    claimed_hours_per_week: 0,
    capacity_hours: null,
    load_band: null,
    load_index: null,
    load_flag: "unknown",
    energy: {
      energizing_hours: 0,
      neutral_hours: 0,
      draining_hours: 0,
      draining_share: 0,
      energy_score: 0,
    },
    misalignment: {
      misaligned_hours: 0,
      low_skill_hours: 0,
      low_enjoyment_hours: 0,
      share: 0,
      risk: "none",
      tasks: [],
    },
    clarity_score: null,
    resolutions: { delegate: [], drop: [], automate: [] },
    top_tasks: [],
  };
}

// ── Hire signal ──────────────────────────────────────────────────────────
// Aggregates force_delegation + MustDelegate hours by pillar/skill. The
// suggested role is a plain-English starting point derived from the top
// pillar (the AI narrative refines it); hours volume is the honest size
// of the gap. Feeds the "Your Next Hire" section + HTVA CTA.

const PILLAR_ROLE_SUGGESTION: Record<string, string> = {
  "Operations & Delivery": "Operations Assistant",
  "Revenue & Sales": "Sales Support Assistant",
  "Growth & Brand": "Marketing Assistant",
  "Strategy & Culture": "Chief of Staff / Senior Coordinator",
  "Non Strategic": "Virtual Assistant (admin)",
  "Administrative & Internal": "Virtual Assistant (admin)",
};

export function computeHireSignal(members: MemberMetrics[], inputs: TeamMemberInput[]): HireSignal {
  type Pill = {
    pillar: string;
    hours: number;
    tasks: { name: string; hours: number }[];
    members: Set<string>;
  };
  const byPillar = new Map<string, Pill>();
  const bySkill = new Map<string, number>();
  let total = 0;
  let taskCount = 0;

  const sessionCtx = { team_size_band: null, gross_revenue_band: null };
  for (const m of inputs) {
    if (!m.participated) continue;
    for (const a of m.activities) {
      const hours = Number(a.hours_per_week) || 0;
      if (hours <= 0) continue;
      const record = {
        ...a,
        currently_doing: a.currently_doing ?? true,
        energy_impact: a.energy_impact ?? 0,
        delegation_plan: { rova_tracker: { diy_hours_monthly: hours } },
      };
      const res = getFinalResolution(record, sessionCtx);
      const wantsOut = a.force_delegation || res === "MustDelegate";
      if (!wantsOut) continue;

      total += hours;
      taskCount += 1;

      const pillar = a.strategic_pillar ? getPillarLabel(a.strategic_pillar) : "Uncategorized";
      const p = byPillar.get(pillar) ?? { pillar, hours: 0, tasks: [], members: new Set<string>() };
      p.hours += hours;
      p.tasks.push({ name: a.activity_name, hours });
      p.members.add(m.member_id);
      byPillar.set(pillar, p);

      const skill = a.skill || "Unspecified";
      bySkill.set(skill, (bySkill.get(skill) ?? 0) + hours);
    }
  }

  const pillarList = Array.from(byPillar.values())
    .sort((a, b) => b.hours - a.hours)
    .map((p) => ({
      pillar: p.pillar,
      hours_per_week: round1(p.hours),
      task_count: p.tasks.length,
      member_count: p.members.size,
      top_tasks: p.tasks
        .sort((a, b) => b.hours - a.hours)
        .slice(0, 5)
        .map((t) => t.name),
    }));

  const skillList = Array.from(bySkill.entries())
    .map(([skill, hours]) => ({ skill, hours_per_week: round1(hours) }))
    .sort((a, b) => b.hours_per_week - a.hours_per_week);

  const topPillar = pillarList[0]?.pillar ?? null;
  return {
    total_hours_per_week: round1(total),
    task_count: taskCount,
    by_pillar: pillarList,
    by_skill: skillList,
    dominant_skill: skillList[0]?.skill ?? null,
    suggested_role:
      total > 0 && topPillar
        ? (PILLAR_ROLE_SUGGESTION[topPillar] ?? "Virtual Assistant (admin)")
        : null,
  };
}

// ── Goal-alignment lens (#8) ──────────────────────────────────────────────

// Classify one "why" by signal strength. STRONG = a real, specific reason;
// WEAK = present but throwaway/too short; MISSING = blank. The deterministic
// layer only measures signal; the AI decides whether a strong-why task truly
// serves the goal.
export function classifyWhySignal(why: string | null | undefined): WhySignal {
  const raw = (why ?? "").trim().replace(/\s+/g, " ");
  if (!raw) return "missing";
  if (raw.length < WHY_MIN_SIGNAL_CHARS || WHY_JUNK_RE.test(raw)) return "weak";
  return "strong";
}

export function computeGoalAlignment(
  participants: TeamMemberInput[],
  goal: TeamGoalInput,
): TeamGoalAlignment {
  const goalText = goal?.goal?.trim() || null;
  const disabled: TeamGoalAlignment = {
    enabled: false,
    goal: null,
    vision: goal?.vision?.trim() || null,
    timeframe: goal?.timeframe?.trim() || null,
    aligned_hours: 0,
    questionable_hours: 0,
    total_hours: 0,
    aligned_share: 0,
    by_member: [],
    flags: [],
    drop_candidates: [],
    note: "No company goal is set, so the goal-alignment lens is turned off. Set a goal on the company to measure the team's work against where the business is headed.",
  };
  if (!goalText) return disabled;

  let alignedHours = 0;
  let questionableHours = 0;
  const flags: GoalAlignmentFlag[] = [];
  const byMember = new Map<string, GoalAlignmentMemberRollup>();

  for (const m of participants) {
    for (const a of m.activities) {
      if (!a.activity_name) continue;
      const hours = Number(a.hours_per_week) || 0;
      if (hours <= 0) continue;
      const signal = classifyWhySignal(a.task_why);
      const roll = byMember.get(m.member_id) ?? {
        member_id: m.member_id,
        member_name: m.name,
        aligned_hours: 0,
        questionable_hours: 0,
      };
      if (signal === "strong") {
        alignedHours += hours;
        roll.aligned_hours = round1(roll.aligned_hours + hours);
      } else {
        questionableHours += hours;
        roll.questionable_hours = round1(roll.questionable_hours + hours);
        flags.push({
          member_id: m.member_id,
          member_name: m.name,
          activity_name: a.activity_name,
          hours_per_week: round1(hours),
          why: (a.task_why ?? "").trim() || null,
          why_signal: signal,
        });
      }
      byMember.set(m.member_id, roll);
    }
  }

  const total = alignedHours + questionableHours;
  flags.sort(
    (a, b) => b.hours_per_week - a.hours_per_week || a.member_name.localeCompare(b.member_name),
  );

  return {
    enabled: true,
    goal: goalText,
    vision: goal?.vision?.trim() || null,
    timeframe: goal?.timeframe?.trim() || null,
    aligned_hours: round1(alignedHours),
    questionable_hours: round1(questionableHours),
    total_hours: round1(total),
    aligned_share: total > 0 ? round2(alignedHours / total) : 0,
    by_member: Array.from(byMember.values()).sort(
      (a, b) => b.questionable_hours - a.questionable_hours,
    ),
    flags,
    drop_candidates: flags.slice(0, GOAL_DROP_CANDIDATE_LIMIT),
    note: null,
  };
}

// ── Manager↔report triangulation (#7) ─────────────────────────────────────

const LOAD_GAP_PHRASE = (band: string): string => band || "unknown";

export function computeInconsistencies(members: TeamMemberInput[]): Inconsistencies {
  // Managers = members who submitted a management read (role_context). Only
  // those can be cross-checked (the read is the thing we compare).
  const managers = members.filter((m) => m.role_context != null);
  if (managers.length === 0) {
    return {
      enabled: false,
      managers: [],
      items: [],
      note: "No manager completed the team-and-management section this round, so there is nothing to cross-check reports against.",
    };
  }

  const byId = new Map(members.map((m) => [m.member_id, m]));
  const reportsOf = (mgrId: string) => members.filter((m) => m.reports_to_id === mgrId);

  const triangulations: ManagerTriangulation[] = [];
  const allItems: LoadInconsistency[] = [];

  for (const mgr of managers) {
    const ctx = mgr.role_context ?? {};
    const mgrLoadBand = typeof ctx.team_load_read === "string" ? ctx.team_load_read : null;
    const mgrClarity5 =
      typeof ctx.team_clarity_read === "number" &&
      ctx.team_clarity_read >= 1 &&
      ctx.team_clarity_read <= 5
        ? ctx.team_clarity_read
        : null;
    const mgrClarity100 = mgrClarity5 !== null ? Math.round(((mgrClarity5 - 1) / 4) * 100) : null;

    const reports = reportsOf(mgr.member_id).filter((r) => byId.has(r.member_id));
    const present = reports.filter((r) => r.participated);
    const missing = reports.filter((r) => !r.participated);
    const mgrInconsistencies: LoadInconsistency[] = [];

    for (const r of present) {
      // LOAD compare
      if (
        mgrLoadBand &&
        r.load_band &&
        mgrLoadBand in LOAD_SEVERITY_ORDER &&
        r.load_band in LOAD_SEVERITY_ORDER
      ) {
        const gap =
          (LOAD_SEVERITY_ORDER[r.load_band] as number) -
          (LOAD_SEVERITY_ORDER[mgrLoadBand] as number);
        const abs = Math.abs(gap);
        if (abs >= LOAD_GAP_NOTABLE) {
          const direction: InconsistencyDirection =
            gap > 0 ? "manager_under_reads" : "manager_over_reads";
          const item: LoadInconsistency = {
            kind: "load",
            manager_id: mgr.member_id,
            manager_name: mgr.name,
            report_id: r.member_id,
            report_name: r.name,
            manager_read: LOAD_GAP_PHRASE(mgrLoadBand),
            report_self: LOAD_GAP_PHRASE(r.load_band),
            gap: abs,
            severity: abs >= LOAD_GAP_STARK ? "stark" : "notable",
            direction,
            detail:
              direction === "manager_under_reads"
                ? `${mgr.name} reads the team's load as "${mgrLoadBand}", but ${r.name} (reports to ${mgr.name}) says "${r.load_band}" — heavier than the manager's read.`
                : `${mgr.name} reads the team's load as "${mgrLoadBand}", but ${r.name} (reports to ${mgr.name}) says "${r.load_band}" — lighter than the manager's read.`,
          };
          mgrInconsistencies.push(item);
        }
      }
      // CLARITY compare
      if (mgrClarity100 !== null) {
        const reportClarity = computeClarityScore(r.role_clarity);
        if (reportClarity !== null) {
          const gap = mgrClarity100 - reportClarity;
          const abs = Math.abs(gap);
          if (abs >= CLARITY_GAP_NOTABLE) {
            const direction: InconsistencyDirection =
              gap > 0 ? "manager_over_reads" : "manager_under_reads";
            const item: LoadInconsistency = {
              kind: "clarity",
              manager_id: mgr.member_id,
              manager_name: mgr.name,
              report_id: r.member_id,
              report_name: r.name,
              manager_read: `${mgrClarity5}/5`,
              report_self: `${reportClarity}/100`,
              gap: abs,
              severity: abs >= CLARITY_GAP_STARK ? "stark" : "notable",
              direction,
              detail:
                direction === "manager_over_reads"
                  ? `${mgr.name} thinks the team is clear on their work, but ${r.name} rates their own role clarity much lower.`
                  : `${mgr.name} reads the team as unclear on their work, but ${r.name} feels clearer about their own role than the manager expects.`,
            };
            mgrInconsistencies.push(item);
          }
        }
      }
    }

    mgrInconsistencies.sort(
      (a, b) => severityRank(b.severity) - severityRank(a.severity) || b.gap - a.gap,
    );
    triangulations.push({
      manager_id: mgr.member_id,
      manager_name: mgr.name,
      team_load_read: mgrLoadBand,
      team_clarity_read: mgrClarity5,
      reports_present: present.length,
      reports_missing: missing.length,
      missing_names: missing.map((r) => r.name),
      inconsistencies: mgrInconsistencies,
    });
    allItems.push(...mgrInconsistencies);
  }

  allItems.sort((a, b) => severityRank(b.severity) - severityRank(a.severity) || b.gap - a.gap);

  return {
    enabled: true,
    managers: triangulations.sort(
      (a, b) =>
        b.inconsistencies.length - a.inconsistencies.length ||
        a.manager_name.localeCompare(b.manager_name),
    ),
    items: allItems,
    note: null,
  };
}

function severityRank(s: InconsistencySeverity): number {
  return s === "stark" ? 2 : 1;
}

// ── The aggregation entry point ──────────────────────────────────────────

export function aggregateTeam(input: TeamAggregationInput): TeamAggregation {
  const participants = input.members.filter((m) => m.participated);
  const nonParticipants = input.members.filter((m) => !m.participated);

  const memberMetrics = input.members.map((m) =>
    m.participated ? computeMemberMetrics(m) : nonParticipantMetrics(m),
  );
  const participantMetrics = memberMetrics.filter((m) => m.participated);

  const invited = input.members.length;
  const completed = participants.length;
  const rate = invited > 0 ? Math.round((completed / invited) * 100) : 0;

  const overloaded = participantMetrics.filter((m) => m.load_flag === "overloaded");
  const underutilized = participantMetrics.filter((m) => m.load_flag === "underutilized");

  // team energy — engine normalization over ALL participant hours
  const allActs = participants.flatMap((m) =>
    m.activities.map((a) => ({
      energy_impact: a.energy_impact ?? 0,
      delegation_plan: {
        rova_tracker: { diy_hours_monthly: Number(a.hours_per_week) || 0 },
      },
    })),
  );
  const teamEnergyScore = computeEnergyScoreBefore(allActs);

  const capacities = participantMetrics
    .map((m) => m.capacity_hours)
    .filter((c): c is number => c !== null);

  const toCandidates = (bucket: "delegate" | "drop" | "automate"): ResolutionCandidate[] =>
    participantMetrics
      .flatMap((m) =>
        m.resolutions[bucket].map((r) => ({
          member_id: m.member_id,
          member_name: m.name,
          activity_name: r.activity_name,
          hours_per_week: r.hours_per_week,
        })),
      )
      .sort((a, b) => b.hours_per_week - a.hours_per_week);

  return {
    participation: {
      invited,
      completed,
      rate,
      low: rate < LOW_PARTICIPATION_THRESHOLD,
      non_participants: nonParticipants.map((m) => ({
        member_id: m.member_id,
        name: m.name,
        role_title: m.role_title,
      })),
    },
    team: {
      total_claimed_hours_per_week: round1(
        participantMetrics.reduce((s, m) => s + m.claimed_hours_per_week, 0),
      ),
      total_capacity_hours_per_week:
        capacities.length > 0 ? round1(capacities.reduce((s, c) => s + c, 0)) : null,
      overloaded_count: overloaded.length,
      underutilized_count: underutilized.length,
      energy: {
        energizing_hours: round1(
          participantMetrics.reduce((s, m) => s + m.energy.energizing_hours, 0),
        ),
        neutral_hours: round1(participantMetrics.reduce((s, m) => s + m.energy.neutral_hours, 0)),
        draining_hours: round1(participantMetrics.reduce((s, m) => s + m.energy.draining_hours, 0)),
        energy_score: teamEnergyScore,
      },
    },
    members: memberMetrics,
    overloaded_members: overloaded,
    underutilized_members: underutilized,
    overlaps: detectOverlaps(participants),
    crack_clusters: clusterCracks(participants),
    misaligned_members: participantMetrics
      .filter((m) => m.misalignment.risk === "high" || m.misalignment.risk === "moderate")
      .map((m) => ({
        member_id: m.member_id,
        name: m.name,
        misaligned_hours: m.misalignment.misaligned_hours,
        share: m.misalignment.share,
        risk: m.misalignment.risk,
        tasks: m.misalignment.tasks,
      }))
      .sort((a, b) => b.misaligned_hours - a.misaligned_hours),
    delegate_candidates: toCandidates("delegate"),
    drop_candidates: toCandidates("drop"),
    automate_candidates: toCandidates("automate"),
    hire_signal: computeHireSignal(participantMetrics, input.members),
    goalAlignment: computeGoalAlignment(participants, input.goal ?? null),
    inconsistencies: computeInconsistencies(input.members),
  };
}
